<script lang="ts">
import { defineComponent } from 'vue'
import CompanyDataService from '@/services/CompanyDataService'
import type Company from '@/types/Company'
import type ResponseData from '@/types/ResponseData'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import * as _ from 'lodash'
import { Table as UiTable, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { TableCard, TableSearch, TablePagination, TableEmptyState } from '@/components/common/table'

export default defineComponent({
  name: 'company-table',
  components: {
    UiTable,
    TableHeader,
    TableBody,
    TableRow,
    TableCell,
    TableCard,
    TableSearch,
    TablePagination,
    TableEmptyState,
  },
  props: ['hladaj'],
  data() {
    return {
      companies: [] as Company[],
      loaded: false,
      pagination: {} as any,
      selectedColumn: 'id',
      selectedDirection: 'desc' as 'asc' | 'desc',
      searchTerm: '',
      pagesRange: 5
    }
  },
  methods: {
    sortBy(column: string) {
      if (this.selectedColumn === column) {
        this.selectedDirection = this.selectedDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.selectedColumn = column;
        this.selectedDirection = 'asc';
      }
      this.retrieveCompanies(1);
    },
    refreshList() {
      this.retrieveCompanies(1);
    },
    retrieveCompanies(page: number) {
      if (this.searchTerm) {
        const id = Number(this.searchTerm);
        if (Number.isInteger(id) && id > 0) {
          CompanyDataService.getCompanyById(id)
            .then((response: ResponseData) => {
              this.companies = [response.data.data];
              this.pagination = { current_page: 1, last_page: 1, total: 1 };
              this.loaded = true;
            })
            .catch((e: Error) => {
              this.companies = [];
              this.pagination = { current_page: 1, last_page: 1, total: 0 };
              this.loaded = true;
              if (e.message !== 'Request failed with status code 404') {
                toast.error('Chyba: ' + e.message);
              }
            });
        } else {
          CompanyDataService.searchCompanies(this.searchTerm)
            .then((response: ResponseData) => {
              this.companies = response.data.data.data;
              this.pagination = response.data.data;
              this.loaded = true;
            })
            .catch((e: Error) => {
              this.loaded = true;
              toast.error('Chyba: ' + e.message);
            });
        }
      } else if (this.hladaj != null && this.hladaj != 'all' && this.hladaj != '') {
        CompanyDataService.getAllCompaniesForUser(this.hladaj)
          .then((response: ResponseData) => {
            this.companies = response.data.data.data;
            this.pagination = response.data.data;
            this.loaded = true;
          })
          .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
          })
      } else {
        CompanyDataService.getAllCompanies(page, this.selectedColumn, this.selectedDirection)
          .then((response: ResponseData) => {
            this.companies = response.data.data.data;
            this.pagination = response.data.data;
            this.loaded = true;
          })
          .catch((e: Error) => {
            this.loaded = true;
            toast.error('Chyba: ' + e.message);
          });
      }
    },
  },
  computed: {
    pages() {
      const currentPage = this.pagination.current_page;
      const lastPage = this.pagination.last_page;
      const startPage = Math.max(1, currentPage - Math.floor(this.pagesRange / 2));
      const endPage = Math.min(lastPage, startPage + this.pagesRange - 1);

      return _.range(startPage, endPage + 1);
    }
  },
  watch: {
    searchTerm() {
      this.retrieveCompanies(1);
    }
  },
  mounted() {
    this.retrieveCompanies(1);
  },
})

</script>

<template>
  <main class="flex-1">
    <div class="py-6">
      <div class="max-w-(--breakpoint-2xl) mx-auto">
        <TableCard title="Spoločnosti">
          <template #controls>
            <div class="flex w-full items-center gap-3 sm:w-auto">
              <div class="flex-1 sm:w-64 sm:flex-initial md:w-72">
                <TableSearch v-model="searchTerm" />
              </div>
            </div>
          </template>
          <div class="max-w-full overflow-x-auto min-w-0">
            <UiTable class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
              <TableHeader
                class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
              >
                <TableRow>
                  <TableCell is-header scope="col"
                    class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center cursor-pointer" :class="{ 'text-brand-500': selectedColumn == 'id' }" @click="sortBy('id')">
                      ID
                      <button>
                        <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 size-3" aria-hidden="true"
                          fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg>
                      </button>
                    </span>
                  </TableCell>
                  <TableCell is-header scope="col" class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center cursor-pointer" :class="{ 'text-brand-500': selectedColumn == 'name' }" @click="sortBy('name')">
                      Názov
                      <button>
                        <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 size-3" aria-hidden="true"
                          fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg></button></span>
                  </TableCell>
                  <TableCell is-header scope="col" class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center cursor-pointer"
                      :class="{ 'text-brand-500': selectedColumn == 'owner' }" @click="sortBy('owner')">
                      Zakladateľ
                      <button>
                        <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 size-3" aria-hidden="true"
                          fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg></button></span>
                  </TableCell>
                  <TableCell is-header scope="col" class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center cursor-pointer"
                      :class="{ 'text-brand-500': selectedColumn == 'status' }" @click="sortBy('status')">
                      Stav
                      <button>
                        <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 size-3" aria-hidden="true"
                          fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg></button></span>
                  </TableCell>
                  <TableCell is-header scope="col" class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center cursor-pointer"
                      :class="{ 'text-brand-500': selectedColumn == 'virtual' }" @click="sortBy('virtual')">
                      Je sídlo virtuálne?
                      <button>
                        <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 size-3" aria-hidden="true"
                          fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg></button></span>
                  </TableCell>
                  <TableCell is-header scope="col" class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center cursor-pointer" :class="{ 'text-brand-500': selectedColumn == 'date' }" @click="sortBy('updated_at')">
                      Dátum zmeny
                      <button>
                        <svg xmlns="http://www.w3.org/2000/svg" class="ml-1 size-3" aria-hidden="true"
                          fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg>
                      </button>
                    </span>
                  </TableCell>
                  <TableCell is-header scope="col" class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 text-right">Edit</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
                <TableRow class="hover:bg-gray-200 dark:hover:bg-gray-700" :class="[company.is_virtual ? 'bg-gray-100 dark:bg-gray-800' : 'bg-white dark:bg-gray-900']"
                  v-for="company in companies" :key="company.id">
                  <TableCell class="px-6 py-4 text-sm font-medium text-gray-800 dark:text-gray-200">{{ company.id }}</TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">{{ company.name }}</TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">
                    {{ company.first_name }} {{ company.last_name ? company.last_name : company.owner }}
                  </TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">{{ company.status }}</TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">
                    <FormKit id="is_virtual" type="checkbox" disabled v-model="company.is_virtual" name="is_virtual"
                      :classes="{ input: 'size-4 focus:ring-brand-500 focus:border-brand-500 block shadow-xs sm:text-sm border-gray-300 rounded-md text-brand-500 dark:border-gray-700' }" />
                  </TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">{{ company.updated_at }}</TableCell>
                  <TableCell class="px-6 py-4 text-sm font-medium text-right dark:text-gray-200">
                    <router-link :to="'/company-detail/' + company.id"
                      class="text-green-500 hover:text-green-700">Edit</router-link>
                  </TableCell>
                </TableRow>
                <TableEmptyState
                  v-if="loaded && !companies?.length"
                  title="Žiadne spoločnosti neboli nájdené"
                  hint="Skúste upraviť hľadanie."
                  colspan="7"
                />
              </TableBody>
            </UiTable>
          </div>
          <template #footer>
            <TablePagination
              v-if="pagination.last_page > 1"
              :current-page="pagination.current_page"
              :total-pages="pagination.last_page"
              @page-change="retrieveCompanies"
            />
          </template>
        </TableCard>
      </div>
    </div>
  </main>
</template>
